/**
 * OrgToggle: a compact pill toggle for the reversible per-org state flags
 * (digital twin, physics twin, demo simulator), rendered as a table cell. The
 * column header carries the label, so the pill itself only shows On/Off + its
 * icon; colour encodes state (green on, muted off) so a whole column scans at a
 * glance. This is the piece that lets the actions stop crowding the name.
 *
 * `disabledReason` holds a pill that may not be switched, and says why: as its
 * tooltip, and to a screen reader.
 */
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/ui/cn";

export function OrgToggle({
  on,
  busy,
  onClick,
  icon: Icon,
  onTitle,
  offTitle,
  disabledReason,
}: {
  on: boolean;
  busy: boolean;
  onClick: () => void;
  icon: LucideIcon;
  onTitle: string;
  offTitle: string;
  disabledReason?: string;
}) {
  return (
    <button
      type="button"
      disabled={busy || disabledReason !== undefined}
      onClick={onClick}
      title={disabledReason ?? (on ? onTitle : offTitle)}
      aria-pressed={on}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        on
          ? "bg-success/10 text-success ring-success/30 hover:bg-success/20"
          : "bg-surface-muted text-on-surface-muted ring-border hover:bg-surface-muted/70 hover:text-on-surface",
      )}
    >
      <Icon className="size-3.5" />
      {on ? "On" : "Off"}
      {disabledReason !== undefined && <span className="sr-only">{`: ${disabledReason}`}</span>}
    </button>
  );
}
